import classNames from 'classnames';
import css from './GroupDetail.module.css';
import { HugeiconsIcon } from '@hugeicons/react';
import { CancelCircleIcon, CheckmarkCircleIcon, Edit02Icon, UserCircleIcon } from '@hugeicons-pro/core-bulk-rounded';
import React, { useCallback, useEffect, useState } from 'react';
import { Form, Avatar, Upload, message } from 'antd';
import { getSignedUrl, updateUserProfile, uploadToS3 } from '../../../services';
import { getCallingCodeOfCountry, getErrorMessage, standardizePhoneNumber } from '../../../utils';
import i18n from 'i18next';
import { useReloadCurrentUser, useSetLoading, useCurrentUser } from '../../../context/hooks';
import { UserType } from '../../../types/user';
import { SNotification } from '../../../services/notification';
import { StringUtils } from '../../../utils/string';
import { FormItem } from '../../../components/Form/FormItem';
import FormInput from '../../../components/Form/FormInput';
import FormInputArea from '../../../components/Form/FormInputArea';
import FormSelectCountry from '../../../components/Form/FormSelectCountry/FormSelectCountry';
import SelectLocalLevel1 from '../../../components/Form/SelectLocalLevel1';
import PhoneNumberFields from '../../../components/Form/PhoneNumberFields';
import { RegexUtils } from '../../../utils/regex';

const ProfileInformation = React.memo(() => {
  const reloadCurrentUser = useReloadCurrentUser();
  const setIsLoading = useSetLoading();
  const currentUser = useCurrentUser();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string>('');

  const onSubmitForm = useCallback(
    async (userInfo) => {
      setEditing(false);
      setIsLoading(true);
      try {
        const {
          firstName,
          lastName,
          phoneNumberUserInput,
          phoneNumberCountryCode,
          email,
          businessName,
          businessWebsite,
          businessAddress,
          businessCountry,
          businessLocalLevel1,
          businessLocalLevel2,
          businessPostalCode,
        } = userInfo;

        let phoneNumber = '';
        if (phoneNumberUserInput && phoneNumberCountryCode) {
          const standardizedInput = `${standardizePhoneNumber(phoneNumberUserInput)}`;
          const phoneNumberCallingCode = getCallingCodeOfCountry(phoneNumberCountryCode);
          phoneNumber = `+${phoneNumberCallingCode}${parseInt(standardizedInput)}`;
        }

        const payload: Record<string, unknown> = {
          firstName,
          lastName,
          phoneNumber,
          phoneNumberUserInput,
          phoneNumberCountryCode,
          email,
        };
        if (currentUser?.type !== UserType.SystemAdmin) {
          Object.assign(payload, {
            businessName,
            businessWebsite,
            businessAddress,
            businessCountry,
            businessLocalLevel1,
            businessLocalLevel2,
            businessPostalCode,
          });
        }
        await updateUserProfile(payload);

        SNotification.success(StringUtils.toFirstCap(i18n.t('msg:updateSuccess')));
        reloadCurrentUser();
      } catch (error) {
        SNotification.error(getErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    },
    [reloadCurrentUser, setIsLoading, currentUser?.type],
  );

  const uploadAvatar = useCallback(
    async ({ file, onError }) => {
      const supportedExts = ['png', 'jpg', 'jpeg'];
      const maxSize = 2;
      const ext = file.name.split('.').pop()?.toLowerCase();

      if (file.size > maxSize * 1024 * 1024) {
        message.error(`Image must be smaller than ${maxSize}MB!`);
        return;
      }
      if (!supportedExts.includes(ext || '')) {
        message.error('You can only upload JPG/PNG file!');
        return;
      }

      setIsLoading(true);
      try {
        const signedUrl = await getSignedUrl(file.name, file.size);
        await uploadToS3(signedUrl, file);
        const finalUrl = signedUrl.substring(0, signedUrl.indexOf('?'));

        await updateUserProfile({ avatar: finalUrl });
        setAvatarUrl(finalUrl);
        SNotification.success('Avatar updated successfully!');
        reloadCurrentUser();
      } catch (err) {
        SNotification.error(getErrorMessage(err));
        onError(err);
      } finally {
        setIsLoading(false);
      }
    },
    [setIsLoading, reloadCurrentUser],
  );

  const beforeAvatarUpload = (file: File) => {
    const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
    if (!isJpgOrPng) {
      message.error('You can only upload JPG/PNG file!');
    }
    return isJpgOrPng;
  };

  const resetFormValues = useCallback(() => {
    if (currentUser) {
      const baseValues = {
        firstName: currentUser.firstName,
        lastName: currentUser.lastName,
        phoneNumberCountryCode: currentUser.phoneNumberCountryCode,
        phoneNumberUserInput: currentUser.phoneNumberUserInput,
        email: currentUser.email,
      };
      const businessValues =
        currentUser.type !== UserType.SystemAdmin
          ? {
              businessName: currentUser.businessName,
              businessWebsite: currentUser.businessWebsite,
              businessAddress: currentUser.businessAddress,
              businessCountry: currentUser.businessCountry,
              businessLocalLevel1: currentUser.businessLocalLevel1,
              businessLocalLevel2: currentUser.businessLocalLevel2,
              businessPostalCode: currentUser.businessPostalCode,
            }
          : {};
      form.setFieldsValue({ ...baseValues, ...businessValues });
      setAvatarUrl(currentUser.avatar || '');
    }
  }, [form, currentUser]);

  useEffect(() => {
    resetFormValues();
  }, [currentUser, resetFormValues]);

  const isSystemAdmin = currentUser?.type === UserType.SystemAdmin;
  const businessCountry = Form.useWatch('businessCountry', form);
  const postalCodeLabel = businessCountry === 'US' ? i18n.t('label:zipCode') : i18n.t('label:postalCode');

  const renderFooterActions = () => (
    <div className={css.cardFooter}>
      {!editing && (
        <button type="button" className={css.btnSecondary} onClick={() => setEditing(true)}>
          <HugeiconsIcon icon={Edit02Icon} size={15} color="#374151" />
          Edit
        </button>
      )}
      {editing && (
        <>
          <button
            type="button"
            className={css.btnSecondary}
            onClick={() => {
              resetFormValues();
              setEditing(false);
            }}
          >
            <HugeiconsIcon icon={CancelCircleIcon} size={15} color="#374151" />
            Cancel
          </button>
          <button type="submit" className={css.btnPrimary}>
            <HugeiconsIcon icon={CheckmarkCircleIcon} size={15} color="#fff" />
            Save
          </button>
        </>
      )}
    </div>
  );

  return (
    <Form validateTrigger={false} onFinish={onSubmitForm} form={form}>
      <div className={css.settingsSection}>
        <div className={css.settingsAside}>
          <div className={css.settingsAsideTitle}>Personal info</div>
          <div className={css.settingsAsideDesc}>Update your photo and personal details.</div>
        </div>

        <div className={classNames(css.card, css.formCard)}>
          <div className={css.cardBody}>
            <div className={css.personalAvatarRow}>
              <Avatar
                className={css.profileAvatar}
                size={80}
                src={avatarUrl || undefined}
                icon={!avatarUrl ? <HugeiconsIcon icon={UserCircleIcon} size={40} color="#94a3b8" /> : undefined}
              />
              <div className={css.avatarCardBody}>
                <div className={css.avatarTitle}>Profile Avatar</div>
                <div className={css.avatarSubtitle}>Upload a business logo for your profile.</div>
                <Upload name="avatar" listType="picture" showUploadList={false} customRequest={uploadAvatar} beforeUpload={beforeAvatarUpload}>
                  <button type="button" className={css.btnPrimary}>
                    {avatarUrl ? 'Change Avatar' : 'Upload Avatar'}
                  </button>
                </Upload>
              </div>
            </div>

            <FormItem name="firstName" rules={[{ required: true, message: i18n.t('msg:pleaseInput', { field: i18n.t('label:firstName') }) }]}>
              <FormInput label={i18n.t('label:firstName')} disabled={!editing} />
            </FormItem>
            <FormItem name="lastName" rules={[{ required: true, message: i18n.t('msg:pleaseInput', { field: i18n.t('label:lastName') }) }]}>
              <FormInput label={i18n.t('label:lastName')} disabled={!editing} />
            </FormItem>

            <PhoneNumberFields disabled={!editing} />
            <FormItem
              name="email"
              rules={[
                {
                  required: true,
                  message: i18n.t('msg:pleaseInput', { field: i18n.t('label:loginID') }),
                },
                {
                  message: i18n.t('msg:fieldInvalid', { field: i18n.t('label:loginID') }),
                  pattern: RegexUtils.email,
                },
              ]}
            >
              <FormInput label={i18n.t('label:loginID')} type="email" disabled={!editing} />
            </FormItem>
          </div>
          {renderFooterActions()}
        </div>
      </div>

      {!isSystemAdmin && (
        <div className={css.settingsSection}>
          <div className={css.settingsAside}>
            <div className={css.settingsAsideTitle}>Profile</div>
            <div className={css.settingsAsideDesc}>Update your portfolio and business details.</div>
          </div>

          <div className={classNames(css.card, css.formCard)}>
            <div className={css.cardBody}>
              <FormItem
                name="businessName"
                rules={[
                  {
                    required: true,
                    message: i18n.t('msg:pleaseInput', { field: i18n.t('label:businessName') }),
                  },
                ]}
              >
                <FormInput label={i18n.t('label:businessName', { defaultValue: 'Business Name' })} disabled={!editing} isRequired={false} />
              </FormItem>
              <FormItem name="businessWebsite">
                <FormInput label={i18n.t('label:businessWebsite', { defaultValue: 'Business Website' })} disabled={!editing} />
              </FormItem>
              <FormItem name="businessAddress" rules={[{ required: true, message: i18n.t('msg:pleaseInput', { field: i18n.t('label:address') }) }]}>
                <FormInputArea label={i18n.t('label:address')} disabled={!editing} rows={3} />
              </FormItem>
              <FormItem name="businessCountry" rules={[{ required: true, message: i18n.t('msg:pleaseSelect', { field: i18n.t('label:country') }) }]}>
                <FormSelectCountry label={i18n.t('label:country')} showCallingCode={false} disabled={!editing} onChange={() => form?.setFieldsValue({ businessLocalLevel1: null })} />
              </FormItem>
              <FormItem name="businessLocalLevel1" rules={[{ required: true, message: i18n.t('msg:pleaseSelect', { field: i18n.t('label:localLevel1') }) }]}>
                <SelectLocalLevel1 className="" label={i18n.t('label:localLevel1')} country={businessCountry} disabled={!editing} />
              </FormItem>
              <FormItem name="businessLocalLevel2" rules={[{ required: true, message: i18n.t('msg:pleaseInput', { field: i18n.t('label:localLevel2') }) }]}>
                <FormInput label={i18n.t('label:localLevel2')} disabled={!editing} />
              </FormItem>
              <FormItem
                name="businessPostalCode"
                rules={[
                  { required: businessCountry && businessCountry !== 'VN', message: i18n.t('msg:pleaseInput', { field: postalCodeLabel }) },
                  { pattern: /^[0-9]+$/, message: i18n.t('msg:fieldInvalid', { field: postalCodeLabel }) },
                ]}
              >
                <FormInput label={postalCodeLabel} disabled={!editing} placeholder="" />
              </FormItem>
            </div>
            {renderFooterActions()}
          </div>
        </div>
      )}
    </Form>
  );
});

export default ProfileInformation;
